import _, { isDate, toNumber } from "lodash";
import { makeAutoObservable, toJS } from "mobx";
import {
  ApiResponse,
  DeliveryMethod,
  IRootStore,
  PaymentMethod,
  PaymentStatus,
} from "../index.d";
import { IAddress, ICartItem, IDistanceCheckResponse } from ".";
import { ApiConfig } from "../../services/api-config";
import {
  compareObjectsExcluding,
  currencyFormatter,
  currentDateString,
  ShowAlert,
} from "../../utils/functions";
import WebStorage from "../../utils/webStorage";
import { WEB_STORAGE_NAMES } from "../../utils/constants";
import Api from "../../services/api";
import {
  ICreateOrderResponse,
  IOrder,
  IOrderItem,
  IOrderTypeDetails,
} from "../order";
import { IAddOnOptionMapping, IProductAddOnMapping } from "../products";
import moment from "moment";

export default class CartStore {
  cartItems: ICartItem[] = [];
  cartItemsCount: number = 0;
  orderNotes: string = "";
  deliveryMethod: DeliveryMethod = DeliveryMethod.Delivery;
  paymentMethod: PaymentMethod = PaymentMethod.Card;
  deliveryAddress?: IAddress;
  orderDateTime: {
    date: string;
    time: string;
  } = { date: currentDateString("YYYY-MM-DD"), time: "" };
  cartTotal: number = 0;
  deliveryCost: number = 0;
  voucherDiscount: number = 0;
  deliveryPostcode: string = "";
  tableNumber: number = 0;
  appliedVoucherCode: string = "";

  private rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
    this.setCartItemsFromWebStorage();
    this.setOrderTypeDetailsFromStorage();
    makeAutoObservable(this);
  }

  getCartItem = (productId: number) => {
    return this.cartItems?.find((p) => p.productId === productId);
  };

  addItemToCart = (
    cartItem: ICartItem,
    itemQty: number,
    fromCartPage: boolean
  ) => {
    if (!cartItem) return;

    try {
      //--check if product already exists
      let existCartItem = this.getCartItem(cartItem.productId);

      if (
        existCartItem &&
        compareObjectsExcluding(cartItem, existCartItem, ["Quantity"])
      ) {
        existCartItem.Quantity += itemQty;
        if (!fromCartPage) ShowAlert.Default("Item quantity updated.");
      } else {
        cartItem.Quantity = itemQty;
        this.cartItems.push(cartItem);
        if (!fromCartPage) ShowAlert.Success("Item added to cart.");
      }
      WebStorage.setStorage(WEB_STORAGE_NAMES.CartItems, this.cartItems);
      this.cartItemsCount = this.cartItems?.length ?? 0;

      this.setCartItemsFromWebStorage();
    } catch (err) {
      ShowAlert.Error("An error occured. Please try again.");
    }
  };

  setCartItemsFromWebStorage = () => {
    this.cartItems =
      WebStorage.getStorage<ICartItem[]>(WEB_STORAGE_NAMES.CartItems) ?? [];
    this.cartItemsCount = this.cartItems?.length ?? 0;
  };

  updateCartItemQuantity = (cartItem: ICartItem, qty: number) => {
    if (cartItem.Quantity + qty < 1) return;
    this.addItemToCart(cartItem, qty, true);
  };

  clearCartItems = (productId: number) => {
    if (productId) {
      let removeIndex = this.cartItems?.findIndex(
        (p) => p.productId === productId
      );
      if (removeIndex >= 0) {
        this.cartItems.splice(removeIndex, 1);
        WebStorage.setStorage(WEB_STORAGE_NAMES.CartItems, this.cartItems);
      }
    } else {
      WebStorage.setStorage(WEB_STORAGE_NAMES.CartItems, []);
      this.cartItems = [];
      this.cartItemsCount = 0;
      this.orderNotes = "";
      this.deliveryMethod = DeliveryMethod.Delivery;
      this.paymentMethod = PaymentMethod.Cash;
      this.deliveryAddress = undefined;
      this.orderDateTime = { date: currentDateString("YYYY-MM-DD"), time: "" };
    }
  };

  setDeliveryMethod = (value: number) => {
    this.deliveryMethod = value;

    let ot = this.getOrderTypeDetails();
    if (ot) {
      ot.deliveryMethod = value;
      this.storeOrderTypeDetails(ot);
    }
  };

  setDeliveryAddress = (value: IAddress) => {
    this.deliveryAddress = value;
  };

  setOrderDateTime = (value: { date: string; time: string }) => {
    this.orderDateTime = value;
  };

  setPaymentMethod = (value: PaymentMethod) => {
    this.paymentMethod = value;
  };

  setTableNumber = (tableNo: number) => {
    this.tableNumber = tableNo;
  };

  setCartTotal = (value: number) => {
    this.cartTotal = value;
  };

  setDeliveryCost = (value: number) => {
    this.deliveryCost = value;
  };

  setVoucherDiscount = (value: number) => {
    this.voucherDiscount = value;
  };

  setAppliedVoucherCode = (code: string) => {
    this.appliedVoucherCode = code;
  };

  placeOrder = (callback: Function) => {
    if (this.cartItems && this.cartItems.length) {
      if (
        (this.deliveryMethod &&
          this.deliveryMethod === DeliveryMethod.Collection &&
          this.orderDateTime) ||
        (this.deliveryMethod === DeliveryMethod.Delivery &&
          this.deliveryAddress &&
          this.deliveryAddress.phoneNumber) ||
        this.deliveryMethod === DeliveryMethod.DineIn
      ) {
        if (
          this.paymentMethod &&
          (this.paymentMethod === PaymentMethod.Cash ||
            this.paymentMethod === PaymentMethod.Card ||
            this.paymentMethod === PaymentMethod.Direct)
        ) {
          var order = {} as IOrder;
          order.orderNote = this.orderNotes;
          order.orderDeliveryType = this.deliveryMethod;
          order.taxAmount = 0;
          order.paymentMethod = this.paymentMethod;
          order.paymentStatus = PaymentStatus.Pending;

          let dt = moment(this.orderDateTime.date).add(
            this.orderDateTime.time,
            "hours"
          );

          if (this.deliveryMethod === DeliveryMethod.Delivery) {
            order.deliveryAddress = this.deliveryAddress;
            order.requestedDeliveryDateTime = dt.format();
          } else if (this.deliveryMethod === DeliveryMethod.Collection) {
            order.requestedCollectionDateTime = dt.format();
          } else if (this.deliveryMethod === DeliveryMethod.DineIn) {
            if (!this.tableNumber) {
              ShowAlert.Error("Please choose a table number.");
              return;
            }
            order.tableNumber = this.tableNumber;
          }

          order.orderItems = this.cartItems.map((p) => {
            let orderItem = {} as IOrderItem;
            orderItem.productId = p.productId;
            orderItem.quantity = p.Quantity;
            orderItem.price = p.sellPrice;
            orderItem.discountPrice = p.discountPrice;
            orderItem.discountForQuantity = p.discountForQuantity;
            if (p.offers?.length) {
              const offer = p.offers[0];
              orderItem.offerType = offer.offerType;
              orderItem.freeProductId = offer.freeAdditionalProductId;
              orderItem.freeProductName = offer.freeProduct?.productName;
            }

            orderItem.addOnsPrice = p.addOnsPrice;
            orderItem.totalAmount =
              p.Quantity *
                (p.discountForQuantity <= p.Quantity && p.discountPrice
                  ? p.discountPrice
                  : p.sellPrice) +
              (p.addOnsPrice ?? 0);
            if (p.productAddOns && p.productAddOns.length) {
              const addOns = p.productAddOns
                .flatMap(({ addOn }: IProductAddOnMapping) => {
                  return addOn.addOnOptions.map(
                    ({ addOnOption }: IAddOnOptionMapping) => {
                      if (addOnOption.choosen)
                        return {
                          name: addOnOption.addOnOptionName,
                          price: addOnOption.addOnOptionPrice ?? 0,
                        };
                    }
                  );
                })
                .filter((ad) => ad);

              orderItem.addOnsPrice = addOns.reduce(
                (a, b) => a + (b?.price ?? 0),
                0
              );
              orderItem.choosenAddOns = addOns
                .map((a) => {
                  return `${a?.name} ${currencyFormatter(a?.price)}`;
                })
                .join(", ");
            }

            return orderItem;
          });

          order.orderTotal = this.cartTotal;
          order.deliveryCost =
            this.deliveryMethod == DeliveryMethod.Delivery
              ? this.deliveryCost
              : 0;
          if (this.voucherDiscount)
            order.voucherDiscount = this.voucherDiscount;
          if (this.appliedVoucherCode)
            order.voucherCode = this.appliedVoucherCode;

          Api.post(
            ApiConfig.REST_URIS.order.placeOrder,
            order,
            (res: ApiResponse<ICreateOrderResponse>) => {
              callback && callback(res);
            }
          );
        } else {
          ShowAlert.Error("Payment information missing.");
        }
      } else {
        ShowAlert.Error("Delivery/Collection information missing");
      }
    } else {
      ShowAlert.Error("Your cart is empty");
    }
  };

  getOrderTypeDetails = () => {
    return WebStorage.getStorage<IOrderTypeDetails>(
      WEB_STORAGE_NAMES.OrderTypeDetails
    );
  };

  storeOrderTypeDetails = (orderTypeDetails: IOrderTypeDetails) => {
    WebStorage.setStorage(WEB_STORAGE_NAMES.OrderTypeDetails, orderTypeDetails);
    this.setOrderTypeDetailsFromStorage();
  };

  setOrderTypeDetailsFromStorage = () => {
    var ot = this.getOrderTypeDetails();
    if (!ot) return;
    this.deliveryMethod = toNumber(ot.deliveryMethod);
    this.orderDateTime = { date: ot.date, time: ot.time };
    if (this.deliveryMethod == DeliveryMethod.Delivery)
      this.deliveryPostcode = ot.postcode;
  };

  checkDistance = (pcode1: string, pcode2: string, callback: Function) => {
    var url =
      ApiConfig.DISTANCE_ROOT_URL +
      "api/" +
      ApiConfig.REST_URIS.distance.calculate(pcode1, pcode2);
    Api.getForExternalApi(url, (res: IDistanceCheckResponse) => {
      callback && callback(res);
    });
  };

  validateCartItems = (callback: Function) => {
    const { productStore } = this.rootStore;
    const { products } = productStore;

    if (!products || !products.length) {
      callback(false);
    }

    let unavailableItems: ICartItem[] = [];

    this.cartItems.forEach((item) => {
      var prod = products.find((p) => p.productId == item.productId);
      if (prod && !prod.hidden && !prod.isDeleted) {
        item.sellPrice = prod.sellPrice;
        item.discountPrice = prod.discountPrice;
        item.discountForQuantity = prod.discountForQuantity;
        item.offers = prod.offers;
        //TODO validate addon product changes
      } else {
        unavailableItems.push(item);
      }
    });

    if (unavailableItems.length) {
      this.cartItems = this.cartItems.filter(
        (ci) => !unavailableItems.find((ui) => ui == ci)
      );
      this.cartItemsCount = this.cartItems.length;
    }

    callback(true);
  };
}
